"use client";

import { Activity, Brain, Compass, Flame, ChartLine as LineChart, Radar } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OtaEcosystemExpertise() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const capabilities = [{
    number: "01",
    title: "OTA Revenue Management",
    description: "Maximize your visibility and conversion rates on major platforms with data-backed listing optimization and strategic inventory management.",
    Icon: LineChart
  }, {
    number: "02",
    title: "Tech Stack Integration",
    description: "Seamlessly connect your property management systems with high-performing booking engines to streamline operations and data flow.",
    Icon: Brain
  }, {
    number: "03",
    title: "Revenue Audits",
    description: "Comprehensive performance reviews to identify hidden revenue leaks and unlock untapped potential in your current pricing strategy.",
    Icon: Radar
  }, {
    number: "04",
    title: "Market Positioning",
    description: "Analyze competitive landscapes and demand curves to ensure your property remains the preferred choice in your local market.",
    Icon: Compass
  }, {
    number: "05",
    title: "Direct Booking Growth",
    description: "Implement proven strategies to shift dependency from third-party channels and increase high-margin direct reservations.",
    Icon: Activity
  }, {
    number: "06",
    title: "Dynamic Pricing Strategy",
    description: "Leverage real-time market intelligence to adjust rates dynamically, ensuring optimal occupancy and RevPAR throughout the year.",
    Icon: Flame
  }];
  return <section data-section-id="4553" id="otaecosystemexpertise" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-5">
            <p className="font-default text-xs tracking-widest uppercase text-secondary mb-6">
              03 / Expertise
            </p>
            <h2 className="font-default font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
              OTA Ecosystem Mastery.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8 flex items-end">
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground/90 max-w-2xl">
              We bridge the gap between complex hospitality technology and bottom-line growth. Our revenue management framework is designed to turn your digital presence into a high-conversion engine for sustainable profit.
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border/60">
          {capabilities.map((cap, i) => {
          const Icon = cap.Icon;
          return <div data-index={i} key={cap.number} className="group relative bg-background border border-transparent hover:border-secondary/40 transition-all duration-500 ease-out p-8 sm:p-10 flex flex-col rounded-lg shadow-md" style={{
            aspectRatio: "4 / 5",
            minHeight: "420px"
          }}>
                {}
                <span className="font-default text-xs tracking-widest text-secondary">
                  {cap.number}
                </span>

                {}
                <div className="relative flex items-center justify-start mt-10 mb-10">
                  <div aria-hidden="true" className="absolute -inset-6 rounded-lg bg-secondary/15 blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
                  <Icon strokeWidth={1} className="relative size-8 text-foreground/90 group-hover:text-secondary transition-colors duration-500" />
                </div>

                {}
                <h3 className="font-default font-semibold text-2xl leading-snug text-foreground mb-4">
                  {cap.title}
                </h3>

                {}
                <p className="font-default text-[15px] leading-relaxed text-muted-foreground/80 mb-8">
                  {cap.description}
                </p>

                {}
                <div className="mt-auto">
                  <Link to="/OurServicesPage" className="inline-flex items-center gap-2 font-default text-xs tracking-widest uppercase text-foreground/80 hover:text-secondary transition-colors duration-300">
                    <span className="relative">
                      <span className="bg-left-bottom bg-gradient-to-r from-secondary to-secondary bg-[length:0%_1px] bg-no-repeat group-hover:bg-[length:100%_1px] transition-[background-size] duration-500 ease-out pb-0.5">
                        Learn More
                      </span>
                    </span>
                    <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </div>;
        })}
        </div>

        {}
        <div className="mt-16 md:mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-border">
          <p className="font-default text-sm text-muted-foreground/80 max-w-xl">
            Ready to optimize your revenue? Partner with HotelRevBaBa for expert guidance.
          </p>
          <Button asChild size="lg" className="bg-secondary text-white hover:bg-secondary/90 font-default text-xs tracking-widest uppercase rounded-lg">
            <Link to="/ContactPage">Book a Consultation</Link>
          </Button>
        </div>
      </div>
    </section>;
}